import { createContext, useContext, useEffect, useId, useState } from 'react';
import './appearance-theme.css';
import './panel-signature.css';

const AppearancePortalContext = createContext(null);
export const useAppearancePortalTheme = () => useContext(AppearancePortalContext);

const hexHsl = hex => {
  const [r, g, b] = hex.slice(1).match(/../g).map(value => parseInt(value, 16) / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), delta = max - min;
  const lightness = (max + min) / 2;
  const saturation = delta ? delta / (1 - Math.abs(2 * lightness - 1)) : 0;
  const hue = !delta ? 0 : max === r ? ((g - b) / delta + (g < b ? 6 : 0)) * 60
    : max === g ? ((b - r) / delta + 2) * 60 : ((r - g) / delta + 4) * 60;
  return `${hue.toFixed(2)} ${(saturation * 100).toFixed(2)}% ${(lightness * 100).toFixed(2)}%`;
};

const safeCss = value => typeof value === 'string' && value.length <= 250000 &&
  !/[@\\<\u0000-\u0008\u000b\u000c\u000e-\u001f]|url\s*\(|expression\s*\(|javascript\s*:/i.test(value);

function selectors(value) {
  const result = []; let start = 0, depth = 0, quote = '';
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    if (quote) { if (char === quote) quote = ''; continue; }
    if (char === '"' || char === "'") quote = char;
    else if (char === '(' || char === '[') depth += 1;
    else if (char === ')' || char === ']') { depth -= 1; if (depth < 0) return []; }
    else if (char === ',' && !depth) { result.push(value.slice(start, i).trim()); start = i + 1; }
  }
  if (quote || depth) return [];
  result.push(value.slice(start).trim());
  return result;
}

// The server accepts flat, network-free CSS. Prefix every selector so one
// customer's saved stylesheet cannot affect an admin screen or another surface.
export function scopeAppearanceCss(css, scope) {
  if (!safeCss(css) || !/^\[data-appearance-scope="[a-zA-Z0-9_-]+"\]$/.test(scope)) return '';
  const source = css.replace(/\/\*[\s\S]*?\*\//g, '').trim();
  let offset = 0, result = '';
  const pattern = /([^{}]+)\{([^{}]*)\}/g;
  let match;
  while ((match = pattern.exec(source))) {
    if (source.slice(offset, match.index).trim()) return '';
    const parts = selectors(match[1]);
    if (!parts.length || parts.some(part => !part || /(^|[\s>+~,])(?:html|body)(?=$|[\s.#:[>+~])|:root|:host|:global/i.test(part))) return '';
    result += `${parts.map(part => `${scope} ${part}`).join(', ')}{${match[2]}}\n`;
    offset = pattern.lastIndex;
  }
  return source.slice(offset).trim() ? '' : result;
}

export default function AppearanceTheme({ theme, children, className = '' }) {
  const id = `appearance-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [systemDark, setSystemDark] = useState(() => Boolean(typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches));
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return undefined;
    const update = () => setSystemDark(query.matches);
    update(); query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  const defaults = { primary: '#0b8a84', background: '#f2f6f9', surface: '#ffffff', text: '#0f2a3d' };
  const signatureDefault = theme?.bootswatch === 'panel-signature' && theme.palette
    && typeof theme.palette === 'object' && !Array.isArray(theme.palette)
    && Object.entries(defaults).every(([key, value]) => typeof theme.palette[key] === 'string' && theme.palette[key].toLowerCase() === value);
  const palette = signatureDefault && systemDark
    ? { primary: '#2bb7af', background: '#0c1c28', surface: '#13293a', text: '#eaf2f7' } : theme?.palette;
  const valid = palette && typeof palette === 'object' && !Array.isArray(palette) &&
    ['primary', 'background', 'surface', 'text'].every(key => typeof palette[key] === 'string' && /^#[a-fA-F0-9]{6}$/.test(palette[key]));
  const variables = valid ? Object.fromEntries(Object.entries(palette).filter(([key]) => ['primary', 'background', 'surface', 'text'].includes(key)).map(([key, value]) => [`--appearance-${key}`, value])) : undefined;
  if (variables) {
    const luminance = palette.primary.slice(1).match(/../g).map(hex => parseInt(hex, 16) / 255)
      .map(value => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4)
      .reduce((total, value, index) => total + value * [0.2126, 0.7152, 0.0722][index], 0);
    variables['--appearance-on-primary'] = 1.05 / (luminance + 0.05) >= 4.5 ? '#ffffff' : '#000000';
    variables['--bs-primary-rgb'] = palette.primary.slice(1).match(/../g).map(hex => parseInt(hex, 16)).join(', ');
    variables['--appearance-primary-hsl'] = hexHsl(palette.primary);
    variables['--appearance-on-primary-hsl'] = hexHsl(variables['--appearance-on-primary']);
  }
  // React context follows portals while DOM inheritance does not. Only the
  // validated palette is carried across; authored CSS stays inside its scope.
  const portalTheme = variables ? {
    contentStyle: {
      ...variables,
      '--popover': hexHsl(palette.surface),
      '--popover-foreground': hexHsl(palette.text),
      '--accent': hexHsl(palette.primary),
      '--accent-foreground': hexHsl(variables['--appearance-on-primary']),
      '--ring': hexHsl(palette.primary),
      backgroundColor: palette.surface,
      color: palette.text,
      borderColor: `color-mix(in srgb, ${palette.text} 14%, ${palette.background})`,
      fontFamily: theme?.bootswatch === 'panel-signature' ? '"Bricolage Grotesque", system-ui, sans-serif' : '"Source Sans Pro", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: '14px',
      lineHeight: '20px',
    },
    textStyle: { fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' },
  } : null;
  const css = scopeAppearanceCss(theme?.custom_css || '', `[data-appearance-scope="${id}"]`);
  return <AppearancePortalContext.Provider value={portalTheme}>
    <div className={`appearance-theme ${className}`} data-appearance-scope={id} data-appearance-theme={theme?.bootswatch} data-has-theme={valid ? 'true' : undefined} style={variables}>
      {css && <style>{css}</style>}{children}
    </div>
  </AppearancePortalContext.Provider>;
}
